import { Icon, Tooltip } from '@pypestream/design-system';
import { FC } from 'react';

import { getEnvDotColor } from '../../utils/get-env-dot-color';
import type { SolutionChannelGroup } from '../../utils/group-channels-by-solution';
import type { PlatformChannel } from '../../utils/map-connection-to-row';
import { NativeClickButton } from './native-click-button';
import {
  buildSolutionGroupViews,
  type SolutionGroupsTestIdPrefix,
} from './shared';
import { SOLUTION_CHANNEL_GROUPS_CSS } from './styles';
import { useSolutionGroupsTranslate } from './use-solution-groups-translate';

export interface SolutionChannelGroupsProps {
  groups: SolutionChannelGroup[];
  /** Scopes the expanded state, see `groupToggleKey`. */
  ownerId: string;
  expandedKeys: Record<string, boolean>;
  onToggle: (key: string) => void;
  onGoToChannel: (channel: PlatformChannel) => void;
  onGoToSolution: (solutionId: string) => void;
  testIdPrefix: SolutionGroupsTestIdPrefix;
}

/**
 * React renderer of the solution boxes for the Edit modal. The table's Lit twin
 * is `renderSolutionChannelGroups`; both map the same view model
 * (`buildSolutionGroupViews`) and inject the same stylesheet. No propagation is
 * stopped here: the modal is not inside a `ps-data-table` row.
 *
 * Every action is a `NativeClickButton`, not a JSX `onClick`: the modal is a
 * `ps-modal`, which moves itself out of the React root while open, and a
 * synthetic `onClick` does not reach a button there (see that component). The
 * Lit twin binds `@click` natively and does not need it.
 */
export const SolutionChannelGroups: FC<SolutionChannelGroupsProps> = ({
  groups,
  ownerId,
  expandedKeys,
  onToggle,
  onGoToChannel,
  onGoToSolution,
  testIdPrefix,
}) => {
  const translate = useSolutionGroupsTranslate();
  return (
    <>
      <style>{SOLUTION_CHANNEL_GROUPS_CSS}</style>
      {buildSolutionGroupViews({
        groups,
        ownerId,
        isExpanded: (key) => !!expandedKeys[key],
        testIdPrefix,
        translate,
      }).map(({ link, ...view }) => (
        <div
          key={view.key}
          className="c-solution-group"
          role={view.regionLabel == null ? undefined : 'region'}
          aria-label={view.regionLabel ?? undefined}
          // `test-id` is the repo's Playwright attribute; React's DOM typings do
          // not know it on native elements.
          {...{ 'test-id': view.testId }}
        >
          {view.unresolved ? null : link ? (
            <Tooltip content={link.tooltip}>
              <NativeClickButton
                className="c-solution-group__chip"
                aria-label={link.ariaLabel}
                onActivate={() => onGoToSolution(link.solutionId)}
              >
                <span className="c-solution-group__label" title={view.name}>
                  {view.name}
                </span>
                <Icon name="arrow-in-circle" size="xsmall" />
              </NativeClickButton>
            </Tooltip>
          ) : (
            <span className="c-solution-group__chip c-solution-group__chip--static">
              <span className="c-solution-group__label" title={view.name}>
                {view.name}
              </span>
            </span>
          )}
          {view.pills.map((pill) => {
            const content = (
              <>
                <span
                  className="c-solution-group__dot"
                  style={{ background: getEnvDotColor(pill.channel.env) }}
                />
                <span className="c-solution-group__label" title={pill.name}>
                  {pill.name}
                </span>
              </>
            );
            // A pill that leads nowhere is text, named by its visible name:
            // no button, tooltip or arrow promising a page (ChannelPillView).
            if (!pill.interactive) {
              return (
                <span
                  key={pill.channel.channelId}
                  className="c-solution-group__pill c-solution-group__pill--static"
                  {...{ 'test-id': pill.testId }}
                >
                  {content}
                </span>
              );
            }
            return (
              <Tooltip key={pill.channel.channelId} content={pill.tooltip}>
                <NativeClickButton
                  className="c-solution-group__pill"
                  test-id={pill.testId}
                  aria-label={pill.ariaLabel}
                  onActivate={() => onGoToChannel(pill.channel)}
                >
                  {content}
                  <Icon name="arrow-in-circle" size="xsmall" />
                </NativeClickButton>
              </Tooltip>
            );
          })}
          {view.more && (
            <NativeClickButton
              className="c-solution-group__more"
              aria-expanded={view.more.expanded}
              onActivate={() => onToggle(view.key)}
            >
              {view.more.label}
              <Icon name={view.more.icon} size="xsmall" />
            </NativeClickButton>
          )}
        </div>
      ))}
    </>
  );
};
